import { DATA_ATTRIBUTE_DRAG_TYPE } from './dataAttributeDragType.constants.tsx';
import type { ViewBoxRect } from './ListEdgeDetails.tsx';
import { toPoint, type Point } from './toPortInfo.tsx';

/** Converts DOM coordinates values to diagram coordinates in order
 * to respect panning, zooming as well as where the root SVG exists
 * in the document */
export const toDiagramPosition = (
	x: number,
	y: number,
	svgElement: HTMLElement | null | undefined,
	zoom: number,
	viewBox: ViewBoxRect,
): Point => {
	const bound = svgElement?.getBoundingClientRect();
	const boundLeft = bound?.left || 0;
	const boundTop = bound?.top || 0;

	return toPoint((x - boundLeft) / zoom + viewBox.left, (y - boundTop) / zoom + viewBox.top);
};

/** Walks up the DOM heirarcy from `target` until it finds the first element
 * with a {@link DATA_ATTRIBUTE_DRAG_TYPE}.
 *
 * If none found or `stopAtAncestor` element is reached, `stopAtAncestor` is returned.
 *
 * @example
 * // Given this heirarchy...
 * <apple>
 *     <banana>
 *         <cherry data-drag-type="foo">
 *             <date>
 *                 <eggplant data-drag-type="bar" />
 *             </date>
 *         </cherry>
 *     </banana>
 * </apple>
 *
 * // returns itself
 * findClosestDraggableAncestor(eggplant, apple);
 * > <eggplant/>
 *
 * // returns ancestor
 * findClosestDraggableAncestor(date, apple);
 * > <cherry/>
 *
 * // returns `stopAtAncestor` value
 * findClosestDraggableAncestor(banana, apple);
 * > <apple/>
 *
 * // returns `stopAtAncestor` value even if nothing found
 * findClosestDraggableAncestor(banana, date);
 * > <date/>
 *
 * */
export const findClosestDraggableAncestor = (
	target: HTMLElement,
	stopAtAncestor: HTMLElement,
): HTMLElement | null => {
	let current: HTMLElement | null = target;

	if (!stopAtAncestor.contains(target)) {
		return null;
	}

	while (current) {
		if (current == null || current === stopAtAncestor) {
			return stopAtAncestor;
		}

		if (current.getAttribute(DATA_ATTRIBUTE_DRAG_TYPE)) {
			return current;
		}

		current = current.parentElement;
	}

	return stopAtAncestor;
};

/**
 * Allows other `target` elements to receive pointer events.
 *
 * For example, rerouting a transition, we want the new port to receive the pointer events.
 * Without this, the original port will have 'captured' the event.
 */
export function releasePointerCaptureIfHeld(e: PointerEvent): void {
	if (e.target && e.target instanceof Element && e.target.hasPointerCapture(e.pointerId)) {
		e.target.releasePointerCapture(e.pointerId);
	}
}
